/**
 * Custom hook for file upload functionality
 * Manages file upload state, validation, and retry logic
 */

import type { UploadedFile } from "@/stores/ChatStore";

import { useState, useRef, useCallback, useEffect } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";

import {
  prepareFilesForUpload,
  removeFileByName,
  hasFilesInProgress,
  calculateUploadProgress,
} from "@/utils/chat/fileOperations";
import {
  createValidationError,
  shouldBlockUpload,
  getFirstFailedFile,
  type ValidationResult,
} from "@/utils/chat/validationHelpers";
import { isRetryableUploadErrorCode, parseFileUploadError } from "@/utils/fileUploadErrorParser";
import { logger } from "@/telemetry";

/** How many times each failed file may be retried before the upload is given up on. */
export const MAX_UPLOAD_RETRIES = 2;

/**
 * Configuration options for the useFileUpload hook
 */
export interface UseFileUploadProps {
  /** Async function to handle the actual file upload to the server */
  onUpload?: (files: File[]) => Promise<UploadedFile[]>;
  /** Optional validation function to check files before upload */
  onValidate?: (files: File[]) => ValidationResult;
  /** Maximum number of files allowed (default: 10) */
  maxFiles?: number;
  /** Accepted file types (not currently used) */
  accept?: string;
}

/**
 * Return type for the useFileUpload hook
 */
export interface UseFileUploadReturn {
  /** Array of currently uploaded/uploading files */
  files: UploadedFile[];
  /** Current upload progress (0-100) */
  uploadProgress: number;
  /** Whether files are currently being uploaded */
  isUploading: boolean;
  /** Array of files that failed to upload */
  failedFiles: File[];
  /** Reference to the file input element */
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  /** Current error state, null if no error */
  error: Error | null;

  // Actions
  /** Handler for file input change events */
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  /** Handler for drag-and-drop file events */
  handleDrop: (acceptedFiles: File[]) => Promise<void>;
  /** Remove a file by its name */
  removeFile: (fileName: string) => void;
  /** Retry uploading the failed files that have retries left */
  retryFailedUploads: () => Promise<void>;
  /** True when the failed files have used all of their retries */
  retryLimitReached: boolean;
  /**
   * False when the failure is one a retry cannot fix (validation, duplicate, unsupported type,
   * too large, quarantine and so on). Retry is then not offered and no retries are counted.
   */
  canRetry: boolean;
  /** Clear all files and reset state */
  clearFiles: () => void;

  // State setters for external control
  /** Manually set the files array (for external state management) */
  setFiles: (files: UploadedFile[]) => void;
  /** Manually set upload progress (for external progress tracking) */
  setUploadProgress: (progress: number) => void;
}

/**
 * Custom hook for managing file uploads with retry logic and progress tracking
 *
 * @param props - Configuration options for the hook
 * @returns Object containing file state, upload handlers, and utility functions
 *
 * @example
 * ```tsx
 * const {
 *   files,
 *   isUploading,
 *   failedFiles,
 *   handleDrop,
 *   retryFailedUploads
 * } = useFileUpload({
 *   onUpload: async (files) => {
 *     // Upload logic here
 *     return uploadedFiles;
 *   },
 *   maxFiles: 5
 * });
 * ```
 */
export const useFileUpload = ({
  onUpload,
  onValidate,
  maxFiles = 10,
  accept: _accept,
}: UseFileUploadProps = {}): UseFileUploadReturn => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [failedFiles, setFailedFiles] = useState<File[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [canRetry, setCanRetry] = useState(true);
  /**
   * Retries used so far, keyed by the File object itself. Two different files can share a name,
   * size and modified time (copies from two folders, or pasted images), so no derived key is
   * unique, whereas a fresh selection always yields new File objects and a retry reuses the old.
   */
  const [retryCounts, setRetryCounts] = useState<Map<File, number>>(() => new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Batches can overlap (a second drop or paste while one uploads), so the
  // uploading flag stays set until every batch has settled.
  const batchesInFlight = useRef(0);
  /**
   * Batches whose upload request has not yet resolved or failed. A batch leaves this count before
   * its "Completed" pause, so the progress bar reaches 100 only when no upload is still running.
   */
  const uploadsPending = useRef(0);
  /**
   * One progress ticker shared by every batch in flight. A ticker per batch would restart the bar
   * at 0 and advance it at double speed whenever a second batch overlapped the first.
   */
  const progressTickerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /**
   * Stop functions for each upload still in flight. Every upload owns its own "processing"
   * timeout, so one upload settling never stops another that is still running.
   */
  const activeTimersRef = useRef(new Set<() => void>());

  // Stop every upload's timers if the component unmounts mid-upload.
  useEffect(() => {
    const active = activeTimersRef.current;
    const ticker = progressTickerRef;

    return () => {
      active.forEach((stop) => stop());
      active.clear();
      if (ticker.current !== null) {
        clearInterval(ticker.current);
        ticker.current = null;
      }
    };
  }, []);

  const forgetRetries = useCallback((fileList: File[]) => {
    setRetryCounts((prev) => {
      if (!fileList.some((file) => prev.has(file))) return prev;

      const next = new Map(prev);

      fileList.forEach((file) => next.delete(file));

      return next;
    });
  }, []);

  const processFiles = useCallback(
    async (fileList: File[], isRetry = false) => {
      if (!onUpload) {
        logger.warn("No upload handler provided");

        return;
      }

      // Validation
      if (onValidate) {
        const validationResult = onValidate(fileList);

        if (shouldBlockUpload(validationResult)) {
          const validationError = createValidationError(validationResult.errors);
          const firstFailed = getFirstFailedFile(validationResult.errors);

          if (firstFailed) {
            setFailedFiles([firstFailed]);
          }
          setError(validationError);
          setCanRetry(false);

          return;
        }
      }

      // Check max files
      if (files.length + fileList.length > maxFiles) {
        setError(new Error(t("maxFilesExceeded", `Maximum ${maxFiles} files allowed`)));

        return;
      }

      // Check for duplicate file names
      const existingFileNames = new Set(files.map((f) => f.name.toLowerCase()));
      const duplicateFiles = fileList.filter((file) =>
        existingFileNames.has(file.name.toLowerCase()),
      );

      if (duplicateFiles.length > 0) {
        const duplicateNames = duplicateFiles.map((f) => f.name).join(", ");

        setFailedFiles(duplicateFiles);
        setError(new Error(`DUPLICATE_FILE: ${duplicateNames}`));
        setCanRetry(false);

        return;
      }

      // A file the user selects afresh starts with a full retry allowance.
      if (!isRetry) {
        forgetRetries(fileList);
      }

      setCanRetry(true);
      batchesInFlight.current += 1;
      uploadsPending.current += 1;
      setIsUploading(true);

      // Simulate progress. Only the first upload in flight starts the bar from 0;
      // a batch that overlaps a running upload joins the progress already shown.
      if (uploadsPending.current === 1) {
        setUploadProgress(0);
        progressTickerRef.current = setInterval(() => {
          setUploadProgress((prev) => calculateUploadProgress(prev, 10, 90));
        }, 200);
      }

      // Called once when this batch's upload request resolves or fails. Returns
      // true when it was the last upload still running, which stops the ticker.
      let uploadSettled = false;
      const settleUpload = () => {
        if (uploadSettled) {
          return false;
        }
        uploadSettled = true;
        uploadsPending.current -= 1;
        if (uploadsPending.current > 0) {
          return false;
        }
        if (progressTickerRef.current !== null) {
          clearInterval(progressTickerRef.current);
          progressTickerRef.current = null;
        }

        return true;
      };

      // Add temporary upload markers. Only this batch's markers are touched
      // below, so an overlapping batch keeps its own pending entries.
      const tempFiles = prepareFilesForUpload(fileList, "uploading");
      const batchIds = new Set(tempFiles.map((f) => f.id));

      setFiles((prev) => [...prev, ...tempFiles]);
      setError(null);

      let stopProgressTimers = () => {};

      try {
        // After initial progress, update status to Processing
        const processingTimeout = setTimeout(() => {
          setFiles((prev) =>
            prev.map((f) =>
              batchIds.has(f.id) && f.status === "uploading" ? { ...f, status: "processing" } : f,
            ),
          );
        }, 1000); // Increased from 500ms to show Uploading longer

        stopProgressTimers = () => {
          clearTimeout(processingTimeout);
          activeTimersRef.current.delete(stopProgressTimers);
        };
        activeTimersRef.current.add(stopProgressTimers);

        // Upload files
        const uploadedFiles = await onUpload(fileList);

        stopProgressTimers();
        if (settleUpload()) {
          setUploadProgress(100);
        }

        // Update status to Completed before replacing
        setFiles((prev) =>
          prev.map((f) => (batchIds.has(f.id) ? { ...f, status: "completed" } : f)),
        );

        // Wait a moment to show the Completed status
        await new Promise((resolve) => setTimeout(resolve, 1500)); // Increased to show Completed longer

        // Replace temp files with uploaded
        setFiles((prev) => [...prev.filter((f) => !batchIds.has(f.id)), ...uploadedFiles]);
        setFailedFiles([]);
        forgetRetries(fileList);
        // Cleanup on success, once no other batch is still uploading
        batchesInFlight.current -= 1;
        if (batchesInFlight.current === 0) {
          setIsUploading(false);
          setUploadProgress(0);
        }
      } catch (error) {
        stopProgressTimers();
        const wasLastUpload = settleUpload();

        // Use flushSync to ensure all state updates are committed before throwing
        flushSync(() => {
          setFailedFiles(fileList);
          // Remove this batch's temp files on error
          setFiles((prev) => prev.filter((f) => !batchIds.has(f.id)));
          // Reset uploading state once no other batch is still uploading
          batchesInFlight.current -= 1;
          if (batchesInFlight.current === 0) {
            setIsUploading(false);
            setUploadProgress(0);
          } else if (wasLastUpload) {
            // The batches still in flight have all uploaded and are showing "Completed".
            setUploadProgress(100);
          }
          setError(error instanceof Error ? error : new Error("Upload failed"));
          setCanRetry(
            isRetryableUploadErrorCode(
              parseFileUploadError(error, fileList[0]?.name ?? "").technicalDetails.errorCode,
            ),
          );
        });
        // We don't re-throw here so the UI can handle the error state
        // throw error;
      }
    },
    [maxFiles, onUpload, onValidate, t, files, forgetRetries],
  );

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files;

      if (!fileList || fileList.length === 0) return;

      const filesArray = Array.from(fileList);

      await processFiles(filesArray);

      // Clear input value to allow re-upload of same files
      if (event.target.value) {
        event.target.value = "";
      }
    },
    [processFiles],
  );

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      await processFiles(acceptedFiles);
    },
    [processFiles],
  );

  const removeFile = useCallback((fileName: string) => {
    setFiles((prev) => removeFileByName(prev, fileName));
    setRetryCounts((prev) => {
      const next = new Map([...prev].filter(([file]) => file.name !== fileName));

      return next.size === prev.size ? prev : next;
    });
  }, []);

  // Files that fail together are retried together, so they always share one count. Reading it
  // with `some` fails closed: no file is ever uploaded past the cap, and no failed file is ever
  // left out of a retry and dropped from the list while the others are retried.
  const retryLimitReached =
    canRetry &&
    failedFiles.length > 0 &&
    failedFiles.some((file) => (retryCounts.get(file) ?? 0) >= MAX_UPLOAD_RETRIES);

  const retryFailedUploads = useCallback(async () => {
    if (failedFiles.length === 0 || !canRetry || retryLimitReached) return;

    const filesToRetry = [...failedFiles];

    // Rebuilt from the files being retried, so counts for files no longer failing are dropped.
    setRetryCounts(
      (prev) => new Map(filesToRetry.map((file) => [file, (prev.get(file) ?? 0) + 1])),
    );
    setFailedFiles([]);
    setError(null);

    await processFiles(filesToRetry, true);
  }, [failedFiles, canRetry, retryLimitReached, processFiles]);

  const clearFiles = useCallback(() => {
    setFiles([]);
    setRetryCounts(new Map());
    setFailedFiles([]);
    setUploadProgress(0);
    setIsUploading(false);
    setError(null);
    setCanRetry(true);
  }, []);

  return {
    files,
    uploadProgress,
    isUploading: isUploading || hasFilesInProgress(files),
    failedFiles,
    fileInputRef,
    error,
    handleFileChange,
    handleDrop,
    removeFile,
    retryFailedUploads,
    retryLimitReached,
    canRetry,
    clearFiles,
    setFiles,
    setUploadProgress,
  };
};
